/**
 * useBulkTagWalletHotspots — S10-07
 *
 * Mutation hook for POST /api/v2/wallets/:id/tag_hotspots
 *
 * FIN-SWEEP-FE-3 (F11): Routed via lib/api-client.ts so the Demo-Token-aware
 * Authorization header is set automatically (lib/api-client.ts:75-102). Method,
 * path, and body shape are unchanged. 422 / other-ok behavior preserved.
 *
 * i18n100-1: BulkTagModal.tsx (Consumer) zeigt `message`/`error` direkt an und
 * ist nicht Owner-Datei dieser Story — die eigentliche Netzwerk-Funktion hier
 * ist eine reine (Non-React-)Utility-Funktion, daher direkter `i18next.t()`-
 * Aufruf statt `useTranslation` (Fix-Muster analog walletUtils.ts).
 */
import { useState, useCallback } from 'react'
import i18next from 'i18next'
import apiClient from '../../lib/api-client'

// /api/v2/wallets/:id/tag_hotspots is not in @heliumtracker/api-types yet,
// hence the `as any` cast (same pattern as usePayments.ts:42-45).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const client = apiClient as any

export type BulkTagResult =
  | { ok: true; tagged: number; tagsApplied: number; hotspotsUpdated: number; message: string }
  | { ok: false; error: string }

interface ApiErrorBody {
  status?: number
  body?: { message?: string; error?: string } | string
  message?: string
}

async function bulkTagWalletHotspotsRequest(
  walletId: number,
  tagIds: number[],
  replaceExisting = false,
): Promise<BulkTagResult> {
  try {
    const result = await client.POST(`/api/v2/wallets/${walletId}/tag_hotspots`, {
      body: { tag_ids: tagIds, replace_existing: replaceExisting },
    })

    if (result.error) {
      return { ok: false, error: i18next.t('wallets:bulkTag.assignError') }
    }

    const json = result.data as {
      data?: { tagged?: number; tags_applied?: number; hotspots_updated?: number }
    }

    const tagged = json?.data?.tagged ?? 0
    const tagsApplied = json?.data?.tags_applied ?? 0
    const hotspotsUpdated = json?.data?.hotspots_updated ?? 0
    const message = i18next.t('wallets:bulkTag.assignSuccess', {
      count: tagged,
      hotspots: hotspotsUpdated,
    })

    return {
      ok: true,
      tagged,
      tagsApplied,
      hotspotsUpdated,
      message,
    }
  } catch (e) {
    // api-client.ts:128 wirft ApiError für >=400. Wir mappen 422 und andere
    // Fehler auf das bestehende { ok: false, error }-Shape. Backend-Body-Text
    // wird NICHT mehr durchgereicht (Cluster-C-Muster: er würde den
    // i18n-Fallback dauerhaft überdecken) — stattdessen immer der lokalisierte
    // Fallback-Key in der aktiven Sprache.
    const err = e as ApiErrorBody
    const status = err?.status

    if (status === 422) {
      return { ok: false, error: i18next.t('wallets:bulkTag.invalidTags') }
    }
    return { ok: false, error: i18next.t('wallets:bulkTag.assignError') }
  }
}

export function useBulkTagWalletHotspots() {
  const [isPending, setIsPending] = useState(false)

  const bulkTag = useCallback(
    async (walletId: number, tagIds: number[], replaceExisting = false): Promise<BulkTagResult> => {
      setIsPending(true)
      try {
        return await bulkTagWalletHotspotsRequest(walletId, tagIds, replaceExisting)
      } finally {
        setIsPending(false)
      }
    },
    [],
  )

  return { bulkTag, isPending }
}
